import { useCallback, useRef } from "react";
import { useShallow } from "zustand/shallow";
import { useVerifyLipaBidhaaMerchant } from "../../../hooks/queries/useLipaBidhaaQueries";
import useLipaBidhaaStore from "../../../stores/lipaBidhaaStore";
import { ALIAS_VERIFICATION_STATUS } from "../config/verificationStatus";
import { extractAxiosErrorMessage } from "../../../services/utils/errorUtils";
import { PAYMENT_TYPE_FIELD_CONFIG } from "../config/lipaBidhaaConfig";

export function useAliasVerification() {
  const { paymentType, verifiedAlias, aliasVerificationStatus } = useLipaBidhaaStore(
    useShallow(({ paymentType, verifiedAlias, aliasVerificationStatus }) => ({
      paymentType,
      verifiedAlias,
      aliasVerificationStatus,
    })),
  );
  const { mutate, isPending: isVerifying } = useVerifyLipaBidhaaMerchant();

  const pendingAliasRef = useRef(null);

  const verify = useCallback(
    (alias) => {
      pendingAliasRef.current = alias;
      const { setAliasVerification } = useLipaBidhaaStore.getState();

      mutate(
        { paymentType, alias },
        {
          onSuccess: (data) => {
            if (pendingAliasRef.current !== alias) return;
            setAliasVerification({
              aliasVerificationStatus: ALIAS_VERIFICATION_STATUS.VERIFIED,
              businessName: data?.merchantName || null,
              verifiedAlias: alias,
            });
          },
          onError: (error) => {
            if (pendingAliasRef.current !== alias) return;
            const message = extractAxiosErrorMessage(error);
            setAliasVerification({
              aliasVerificationStatus: ALIAS_VERIFICATION_STATUS.UNVERIFIED,
              businessName: null,
              verifiedAlias: alias,
              aliasVerificationMessage: message,
            });
          },
        },
      );
    },
    [paymentType, mutate],
  );

  // Resets verification state when the user edits the field
  const handleAliasChange = useCallback(
    (value) => {
      const trimmed = value.trim();
      if (trimmed !== verifiedAlias) {
        useLipaBidhaaStore.getState().resetVerification();
      }
    },
    [verifiedAlias],
  );

  // Fires the API call when the user leaves the field
  const handleAliasBlur = useCallback(
    (value) => {
      const trimmed = value.trim();

      // Already verified with this exact value — nothing to do
      if (trimmed === verifiedAlias) return;

      const { aliasMinLength, aliasMaxLength, aliasNumericOnly } =
        PAYMENT_TYPE_FIELD_CONFIG[paymentType] ?? {};
      const isValidLength =
        trimmed.length >= aliasMinLength && trimmed.length <= aliasMaxLength;
      const isAllDigits = !aliasNumericOnly || /^\d+$/.test(trimmed);

      if (isValidLength && isAllDigits) {
        verify(trimmed);
      }
    },
    [paymentType, verifiedAlias, verify],
  );

  const isVerified = aliasVerificationStatus === ALIAS_VERIFICATION_STATUS.VERIFIED;

  return { handleAliasChange, handleAliasBlur, isVerifying, isVerified };
}
